import React, { useEffect, useState } from "react"
import { faClipboard, faClose, faLocationDot, faTag } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { CanView } from "../../../components/CanView/CanView"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import { INPUT_SIZE } from "../../../configuration/InputSize"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { useError } from "../../../context/useErrorContext"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { Permissions } from "../../../Enum/PermissionsType"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { useRegister } from "../../../hooks/Register/useRegister"
import { useCreateServicePoint } from "../../../hooks/ServicePoints/useCreateServicePoint"
import { useRemoveServicePoint } from "../../../hooks/ServicePoints/useRemoveServicePoint"
import { useSaveServicePoint } from "../../../hooks/ServicePoints/useSaveServicePoint"
import { useServicePointSelfService } from "../../../hooks/ServicePoints/useServicePointSelfService"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { ServicePoints } from "../../../Objects/ServicePoint/ServicePoints"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import GetSpace from "../../../utils/space"
import { ServicePointOrdersPopup } from "./ServicePointOrdersPopup"
import { API_ROUTES } from "../../../configuration/RoutesApi"
import { Check, Clock3, Copy, Link2, LoaderCircle, QrCode, ShieldCheck, Tablet, Unlink } from "lucide-react"

interface ServicePointsPupopProps {
  servicePointData?: ServicePoints
  title: string
  onClose: () => void
}

export const ServicePointsPupop: React.FC<ServicePointsPupopProps> = ({ servicePointData, title, onClose }) => {
  const { t } = useTranslation("servicePoints")

  const canEdit = usePermission(Permissions.CanEditeServicePoints)

  const { setError } = useError()

  const { setNewConfirmation } = useConfirmation()

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const { handleSave } = useSaveServicePoint()

  const { handleRemove } = useRemoveServicePoint()

  const [showOrders, setShowOrders] = useState(false)

  const [activationCode, setActivationCode] = useState("")

  const [isTabletLinked, setIsTabletLinked] = useState(servicePointData?.isSelfServiceTabletLinked ?? false)

  const [hasPendingTabletActivation, setHasPendingTabletActivation] = useState(
    servicePointData?.hasActiveSelfServiceActivationCode ?? false,
  )

  const [tabletLastSeenAtUtc, setTabletLastSeenAtUtc] = useState(servicePointData?.selfServiceLastSeenAtUtc ?? "")

  const [tabletAction, setTabletAction] = useState<"generate" | "revoke" | null>(null)

  const [showTabletQrCode, setShowTabletQrCode] = useState(false)

  const [tabletLinkCopied, setTabletLinkCopied] = useState(false)

  const { handleCreateActivationCode, handleRevokeTablet } = useServicePointSelfService()

  const storeSlug = companyMemory.slug?.trim() ?? ""

  const tabletUrl = storeSlug ? `${API_ROUTES.SITE}${storeSlug}/autoatendimento` : ""

  const tabletActivationUrl = tabletUrl && activationCode ? `${tabletUrl}?activationCode=${activationCode}` : ""

  const tabletQrCodeUrl = tabletActivationUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=480x480&ecc=H&data=${encodeURIComponent(tabletActivationUrl)}`
    : ""

  const {
    Name: servicePointName,
    Description: description,
    Status: status,
    handleSubmitForm,
    handleServicePointNameChange,
    handleDescriptionChange,
    handleStatusChange,
    handleInitialValue,
  } = useCreateServicePoint()

  useEffect(() => {
    handleInitialValue(servicePointData)

  }, [handleInitialValue, servicePointData])

  useEffect(() => {
    setIsTabletLinked(servicePointData?.isSelfServiceTabletLinked ?? false)
    setHasPendingTabletActivation(servicePointData?.hasActiveSelfServiceActivationCode ?? false)
    setTabletLastSeenAtUtc(servicePointData?.selfServiceLastSeenAtUtc ?? "")
  }, [servicePointData])

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    const result = handleSubmitForm()

    if ("errors" in result) {
      setError(t("general:label_invalid"), result.errors.map((error) => error.message).join("\n"))
      return
    }

    void handleSave(result, onClose)
  }

  const createTabletActivationCode = async () => {
    if (!servicePointData || !canEdit || isTabletLinked || tabletAction) return

    setTabletAction("generate")

    await handleCreateActivationCode(servicePointData.id, (activation) => {
      setActivationCode(activation.activationCode)
      setHasPendingTabletActivation(true)
      setShowTabletQrCode(false)
    })

    setTabletAction(null)
  }

  const revokeTablet = async () => {
    if (!servicePointData || !canEdit || !isTabletLinked || tabletAction) return

    setTabletAction("revoke")

    await handleRevokeTablet(servicePointData.id, () => {
      setActivationCode("")
      setIsTabletLinked(false)
      setHasPendingTabletActivation(false)
      setTabletLastSeenAtUtc("")
      setShowTabletQrCode(false)
    })

    setTabletAction(null)
  }

  const confirmRevokeTablet = () => {
    if (!servicePointData || !canEdit || !isTabletLinked) return

    setNewConfirmation(
      t("servicePointSelfService:unlink_confirmation_title"),
      t("servicePointSelfService:unlink_confirmation_description", { name: servicePointData.name }),
      revokeTablet,
    )
  }

  const copyTabletLink = async () => {
    if (!tabletActivationUrl) return

    try {
      await navigator.clipboard.writeText(tabletActivationUrl)
      setTabletLinkCopied(true)
      window.setTimeout(() => setTabletLinkCopied(false), 2200)
    } catch {
      setError(t("general:label_title_erro"), t("servicePointSelfService:copy_link_error"))
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              iconColor={THEME.colors.iconBGSecondaryColor}
              title={title}
              icon={faLocationDot}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  {servicePointData && (
                    <div style={{ width: "100px", marginRight: "5px" }}>
                      <GenericDropDown
                        data={Object.mapEnumToStrings(EEnableStatusType, { exclude: [EEnableStatusType.All] })}
                        initialState={status}
                        disable={!canEdit}
                        handlerFuncion={handleStatusChange}

                  descriptionKey="interactionDescriptions:pages.fluxo.servicePoints.servicePointsPupop.select.select18" />
                    </div>
                  )}

                  <PopupHelpButton
                    helpKey="pages.fluxo.servicePoints.servicePointspupop.1"
                    variant="global"
                    title={t("helpPopup:global.service_point.title")}
                    description={t("helpPopup:global.service_point.description")}
                    bullets={t("helpPopup:global.service_point.bullets", { returnObjects: true }) as string[]}
                  />

                  <GetSpace right={5} />

                  <ButtonIcon
                    icon={faClose}
                    size="small"
                    typeButton="button"
                    onClick={onClose}
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}

                  descriptionKey="interactionDescriptions:pages.fluxo.servicePoints.servicePointsPupop.button.onClose" />
                </div>
              }
            />

            <GetSpace top={10} />

            <Animate type={AnimationTypes.fadeInTraslate}>
              <form onSubmit={handleSubmit} className="p-1">
                <InputField
                  type="text"
                  value={servicePointName}
                  icon={faTag}
                  maxLength={INPUT_SIZE.SERVICE_POINT.name}
                  placeholder={t("label_service_point_name_place_holder")}
                  onInvalidMessage={t("label_service_point_name_onInvalidMessage")}
                  onChange={handleServicePointNameChange}
                  disable={!canEdit}
                  required

                  descriptionKey="interactionDescriptions:pages.fluxo.servicePoints.servicePointsPupop.input.tLabelServicePointNamePlaceHolder" />

                <GetSpace top={8} />

                <InputField
                  type="text"
                  value={description}
                  icon={faClipboard}
                  maxLength={INPUT_SIZE.SERVICE_POINT.description}
                  placeholder={t("label_service_point_description_place_holder")}
                  onInvalidMessage={t("label_service_point_description_onInvalidMessage")}
                  onChange={handleDescriptionChange}
                  disable={!canEdit}

                  descriptionKey="interactionDescriptions:pages.fluxo.servicePoints.servicePointsPupop.input.tLabelServicePointDescriptionPlaceHolder" />

                <div className={defaultStyle.divisionBar}></div>

                {servicePointData && companyMemory.isServicePointSelfServiceEnabled && (
                  <>
                    <section className="mb-4 overflow-hidden rounded-lg border-2 shadow-sm">
                      <Animate type={AnimationTypes.fadeInTraslate} duration="0.8s">
                        <div className="flex items-start gap-3 border-b border-orange-200 px-4 py-4">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-orange-600 text-white shadow-md shadow-orange-200">
                            <Tablet size={22} />
                          </div>

                          <div className="min-w-0">
                            <p className="text-xs font-black uppercase tracking-widest text-orange-700">
                              {t("servicePointSelfService:title")}
                            </p>

                            <p className="mt-1 text-xs font-semibold text-slate-600">
                              {t("servicePointSelfService:management_description")}
                            </p>
                          </div>
                        </div>
                      </Animate>

                      <div className="p-4">
                        {isTabletLinked ? (
                          <Animate key="tablet-linked" type={AnimationTypes.slideUpIn} duration="0.7s">
                            <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-4">
                              <div className="flex items-start gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm">
                                  <ShieldCheck size={21} />
                                </div>

                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-black uppercase tracking-widest text-emerald-800">
                                    {t("servicePointSelfService:linked")}
                                  </p>

                                  {tabletLastSeenAtUtc && (
                                    <p className="mt-3 flex items-center gap-2 text-[11px] font-bold text-emerald-800">
                                      <Clock3 size={14} />

                                      {t("servicePointSelfService:last_activity", {
                                        date: new Date(tabletLastSeenAtUtc).toLocaleString("pt-BR"),
                                      })}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <button
                                type="button"
                                disabled={!canEdit || tabletAction !== null}
                                onClick={confirmRevokeTablet}
                                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-3 text-[11px] font-black uppercase tracking-widest text-white shadow-md transition-all hover:bg-red-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"

                  data-interaction-description-key="interactionDescriptions:pages.fluxo.servicePoints.servicePointsPupop.button.confirmRevokeTablet">
                                {tabletAction === "revoke" ? (
                                  <LoaderCircle size={17} className="animate-spin" />
                                ) : (
                                  <Unlink size={17} />
                                )}
                                {tabletAction === "revoke"
                                  ? t("servicePointSelfService:unlinking")
                                  : t("servicePointSelfService:unlink")}
                              </button>
                            </div>
                          </Animate>
                        ) : (
                          <Animate key="tablet-available" type={AnimationTypes.slideUpIn} duration="0.7s">
                            <div className="p-1">
                              <div className="flex items-start gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-600 text-white shadow-sm">
                                  <Link2 size={20} />
                                </div>

                                <div>
                                  <p className="text-xs font-black uppercase tracking-widest text-sky-800">
                                    {hasPendingTabletActivation
                                      ? t("servicePointSelfService:waiting_activation")
                                      : t("servicePointSelfService:not_linked")}
                                  </p>

                                  <p className="mt-1 text-xs font-semibold text-sky-700">
                                    {hasPendingTabletActivation
                                      ? activationCode
                                        ? t("servicePointSelfService:waiting_activation_description")
                                        : t("servicePointSelfService:waiting_activation_hidden_code_description")
                                      : t("servicePointSelfService:not_linked_description")}
                                  </p>
                                </div>
                              </div>

                              {activationCode && (
                                <Animate type={AnimationTypes.zoomIn} duration="0.55s">
                                  <div className="mt-4 bg-white p-4 text-slate-700 shadow-sm">
                                    <p className="text-center text-3xl font-black tracking-[0.22em] text-slate-900">
                                      {activationCode}
                                    </p>

                                    <p className="mt-2 text-center text-[11px] font-bold text-slate-500">
                                      {t("servicePointSelfService:activation_expires")}
                                    </p>

                                    <p className="mt-3 break-all rounded-lg bg-slate-50 px-3 py-2 text-center text-[11px] font-semibold text-slate-600">
                                      {tabletActivationUrl}
                                    </p>

                                    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                      <button
                                        type="button"
                                        disabled={!tabletActivationUrl}
                                        onClick={() => void copyTabletLink()}
                                        className="flex items-center justify-center gap-2 rounded-lg border-2 border-slate-200 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-700 transition-all hover:border-orange-400 hover:text-orange-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"

                  data-interaction-description-key="interactionDescriptions:pages.fluxo.servicePoints.servicePointsPupop.button.voidCopyTabletLink">
                                        {tabletLinkCopied ? <Check size={15} /> : <Copy size={15} />}
                                        {tabletLinkCopied
                                          ? t("servicePointSelfService:link_copied")
                                          : t("servicePointSelfService:copy_link")}
                                      </button>

                                      <button
                                        type="button"
                                        disabled={!tabletQrCodeUrl}
                                        onClick={() => setShowTabletQrCode((current) => !current)}
                                        className="flex items-center justify-center gap-2 rounded-lg border-2 border-slate-200 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-700 transition-all hover:border-orange-400 hover:text-orange-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"

                  data-interaction-description-key="interactionDescriptions:pages.fluxo.servicePoints.servicePointsPupop.button.setShowTabletQrCodeCurrentCurrent">
                                        <QrCode size={15} />
                                        {showTabletQrCode
                                          ? t("servicePointSelfService:hide_qr_code")
                                          : t("servicePointSelfService:show_qr_code")}
                                      </button>
                                    </div>

                                    {showTabletQrCode && (
                                      <Animate type={AnimationTypes.zoomIn} duration="0.45s">
                                        <div className="mt-4 bg-white p-3 text-center">
                                          <img
                                            src={tabletQrCodeUrl}
                                            alt={t("servicePointSelfService:qr_code_alt")}
                                            className="mx-auto aspect-square w-full max-w-56 rounded-lg"
                                          />

                                          <p className="mt-2 text-[10px] font-bold text-slate-500">
                                            {t("servicePointSelfService:qr_code_hint")}
                                          </p>
                                        </div>
                                      </Animate>
                                    )}
                                  </div>
                                </Animate>
                              )}

                              <button
                                type="button"
                                disabled={!canEdit || tabletAction !== null}
                                onClick={() => void createTabletActivationCode()}
                                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-3 text-[11px] font-black uppercase tracking-widest text-white shadow-md transition-all hover:bg-orange-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"

                  data-interaction-description-key="interactionDescriptions:pages.fluxo.servicePoints.servicePointsPupop.button.voidCreateTabletActivationCode">
                                {tabletAction === "generate" ? (
                                  <LoaderCircle size={17} className="animate-spin" />
                                ) : (
                                  <Link2 size={17} />
                                )}
                                {tabletAction === "generate"
                                  ? t("servicePointSelfService:generating_code")
                                  : hasPendingTabletActivation
                                    ? t("servicePointSelfService:generate_new_code")
                                    : t("servicePointSelfService:generate_code")}
                              </button>
                            </div>
                          </Animate>
                        )}
                      </div>
                    </section>

                    <div className={defaultStyle.divisionBar}></div>
                  </>
                )}

                <div className={defaultStyle.divisionBar}></div>

                <div className={defaultStyle.buttonsController}>
                  <Button
                    label={t("button_fetch_relations")}
                    typeButton="button"
                    type="primary"
                    bgColor={THEME.colors.buttonBGSaveColor}
                    onClick={() => setShowOrders(true)}

                  descriptionKey="interactionDescriptions:pages.fluxo.servicePoints.servicePointsPupop.button.tButtonFetchRelations" />

                  <CanView permission={Permissions.CanEditeServicePoints}>
                    <Button
                      label={t("buttons:button_save")}
                      typeButton="submit"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGSaveColor}

                  descriptionKey="interactionDescriptions:pages.fluxo.servicePoints.servicePointsPupop.button.tButtonsButtonSave" />
                  </CanView>

                  {servicePointData && (
                    <>
                      <GetSpace right={5} />

                      <Button
                        label={t("buttons:button_register")}
                        typeButton="button"
                        type="primary"
                        size="medium"
                        bgColor={THEME.colors.buttonBGRegisterColor}
                        onClick={handleEnableRegister}

                  descriptionKey="interactionDescriptions:pages.fluxo.servicePoints.servicePointsPupop.button.tButtonsButtonRegister" />
                    </>
                  )}

                  {servicePointData && (
                    <CanView permission={Permissions.CanEditeServicePoints}>
                      <GetSpace right={5} />

                      <Button
                        label={t("buttons:button_remove")}
                        typeButton="button"
                        type="primary"
                        size="medium"
                        bgColor={THEME.colors.buttonBGRemoveColor}
                        onClick={() =>
                          setNewConfirmation(
                            t("general:label_title_remove"),
                            t("general:label_description_remove"),
                            () => handleRemove(servicePointData, onClose),
                          )
                        }

                  descriptionKey="interactionDescriptions:pages.fluxo.servicePoints.servicePointsPupop.button.tButtonsButtonRemove" />
                    </CanView>
                  )}
                </div>
              </form>
            </Animate>
          </div>
        </Animate>

        {isRegisterEnable && servicePointData && (
          <ViewRegister id={servicePointData.id} typeEntity="ServicePointEntityId" onClose={handleDisableRegister} />
        )}

        {showOrders && servicePointData && (
          <ServicePointOrdersPopup servicePointData={servicePointData} onClose={() => setShowOrders(false)} />
        )}
      </div>
    </ModalPortal>
  )
}
